import apolloClient from './client';
import {
  CapaErrorCode,
  CapaServiceType,
  RefreshDocument,
  RefreshMutation
} from '@generated/graphql';
import {
  STORAGE_ACTION,
  getStorageItem,
  clearStorageAll
} from '@utils/storage';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import AppConfig from '@config';
import { updateToken, logout } from '../modules/client/index';
import { store } from '../store';
import { JwtPayload } from 'jwt-decode';
import * as Sentry from '@sentry/react';

import { FetchResult } from '@apollo/client';

export const GRAPHQL_URL = AppConfig.graphqlUrl;

// support http protocol for local servers
const protocol = AppConfig.mode === 'local' ? 'ws' : 'wss';
const subscriptionEndpoint = window.location.host.includes('bluegreen')
  ? `${protocol}://capa-api-bluegreen.capa.ai/v1/graphql`
  : `${protocol}://${GRAPHQL_URL}`;
export const subscriptionClient = new SubscriptionClient(subscriptionEndpoint, {
  lazy: true,
  reconnect: true,
  reconnectionAttempts: 5,
  connectionParams: () => {
    const accessToken =
      getStorageItem(STORAGE_ACTION.CLIENT_ACCESS_TOKEN) ?? null;

    return {
      'Authorization': `Bearer ${accessToken}`
    };
  }
});

export const clearAuthInfo = () => {
  store?.dispatch(logout());
  clearStorageAll();
  window.location.href = `${AppConfig.baseUrl}/login`;
};

export const tokenRefresh = async () => {
  const refreshToken = localStorage.getItem(
    STORAGE_ACTION.CLIENT_REFRESH_TOKEN
  );
  const keepLoggedInToken = localStorage.getItem(STORAGE_ACTION.KEEP_LOGGED_IN);

  if (!keepLoggedInToken && !refreshToken) return clearAuthInfo();

  try {
    const { data } = await apolloClient.mutate<RefreshMutation>({
      mutation: RefreshDocument,
      variables: {
        refreshToken: (keepLoggedInToken || refreshToken) as string
      }
    });

    if (data?.refresh.result !== CapaErrorCode.Success) return null;

    const newAccessToken = data.refresh.data?.accessToken;
    const newRefreshToken = data.refresh.data?.refreshToken;

    if (!newAccessToken || !newRefreshToken) return null;

    store.dispatch(
      updateToken({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken
      })
    );

    return newAccessToken;
  } catch {
    clearAuthInfo();

    return null;
  }
};

export const setSentryUserInfo = (
  decodedJwtToken?: JwtPayload,
  apiName?: string
) => {
  if (!decodedJwtToken) return;

  const email = decodedJwtToken?.aud?.toString() ?? '';

  const userinfo = {
    email: email,
    decodedJwt: decodedJwtToken
  };

  Sentry.setTag('Email', email);
  Sentry.setUser(userinfo);

  Sentry.setTag('Service', CapaServiceType.Quotation);
  Sentry.setTag('Api', apiName);
};

export const getOperationName = (operation: FetchResult) => {
  const operationName = operation.data ? Object.keys(operation.data)[0] : '';

  return operationName;
};
